import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../Hooks/AuthProvider";
import LoadingSpinner from "./LoadingSpinner";
import { useState, useEffect } from "react";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";

/**
 * Protected route for employee users only
 * Customer users are redirected to customer portal
 */
export const ProtectedRoute = ({ children }) => {
  // 1. Get not just the user, but also the loading state
  const { user, loading } = useAuth();
  const [userRole, setUserRole] = useState(null);
  const [roleLoading, setRoleLoading] = useState(true);

  // 2. Get the current location to remember where the user wanted to go
  const location = useLocation();

  // Initialize Firebase Functions
  useEffect(() => {
    const functions = getFunctions();
    if (process.env.NODE_ENV === "development") {
      connectFunctionsEmulator(functions, "localhost", 5001);
    }
  }, []);

  // Get user role when user is authenticated
  useEffect(() => {
    const getUserRole = async () => {
      if (!user) {
        setRoleLoading(false);
        return;
      }

      try {
        const functions = getFunctions();
        const getUserRoleFunction = httpsCallable(functions, "getUserRole");
        const response = await getUserRoleFunction();
        setUserRole(response.data.role);
      } catch (error) {
        console.error("[ProtectedRoute] Error getting user role:", error);
        // Default to employee if error (for backward compatibility)
        setUserRole("employee");
      } finally {
        setRoleLoading(false);
      }
    };

    getUserRole();
  }, [user]);

  // 3. While authentication is still loading, show the loading spinner.
  //    This is the crucial step that prevents the redirect "flicker".
  if (loading || roleLoading) {
    return <LoadingSpinner />;
  }

  // 4. Once loading is finished, if there's no user, redirect to the login page.
  //    IMPORTANT: We pass the 'location' in the state so the LogIn page knows
  //    where to send the user back to after they log in.
  //    OFFLINE MODE: allow access when offline so cached data can render
  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (!user && !isOffline) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // 5. If user is a customer, redirect them to customer portal
  if (user && userRole === "customer") {
    return <Navigate to="/customer/project-equipment" replace />;
  }

  // 6. If loading is done and there is a user (and they're not a customer), render the child component.
  return children;
};